import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
 IconChevronDown, IconCopy, IconRefresh, IconCirclePlus, IconTrash,
 IconRotateClockwise, IconUpload, IconDownload, IconLink, IconX, IconExternalLink
} from '@tabler/icons-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Spinner } from '../../ui/spinner';
import { Alert, AlertDescription } from '../../ui/alert';
import { apiClient } from '../../../config/apiConfig';
import toast from 'react-hot-toast';
import { useDirtyNavigationGuard } from '../../routing/DirtyNavigationGuard';

const REPORT_ORIGIN = (process.env.REACT_APP_PUBLIC_REPORT_BASE_URL || 'https://report.wetakeover.xyz').replace(/\/+$/, '');

const REPORT_SECTIONS = [
 { id: 'access', label: 'Access', description: 'Publish state, access code, and public link' },
 { id: 'content', label: 'Content', description: 'Client details and report messages' },
 { id: 'included', label: 'Included data', description: 'Campaigns, creators, and group reports' },
 { id: 'advanced', label: 'Advanced', description: 'Brand presentation and comparison data' },
];

export const buildReportUrl = (slug, canonicalUrl = '') => {
 if (!slug) return '';
 if (canonicalUrl) {
 try {
 const parsed = new URL(canonicalUrl);
 if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password) {
 throw new Error('Unsafe report URL');
 }
 const segments = parsed.pathname.split('/').filter(Boolean);
 if (segments.length) segments[segments.length - 1] = slug;
 else segments.push(slug);
 parsed.pathname = `/${segments.map(segment => encodeURIComponent(decodeURIComponent(segment))).join('/')}`;
 parsed.search = '';
 parsed.hash = '';
 return parsed.toString();
 } catch (_) { /* Fall back to the established report origin. */ }
 }
 return `${REPORT_ORIGIN}/${encodeURIComponent(slug)}`;
};

const toDateInput = (value) => {
 if (!value) return '';
 if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
 const date = new Date(value);
 if (Number.isNaN(date.getTime())) return '';
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, '0');
 const day = String(date.getDate()).padStart(2, '0');
 return `${year}-${month}-${day}`;
};

const fromDateInput = (value) => {
 if (!value) return '';
 const [year, month, day] = value.split('-');
 return new Date(Number(year), Number(month) - 1, Number(day))
 .toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const DEFAULT_CONFIG = {
 enabled: false,
 accessCode: '',
 slug: '',
 campaignTitle: '',
 clientName: '',
 campaignTypeLabel: 'Timeline Takeover',
 heroMessage: '',
 heroMessageInProgress: '',
 reportDate: '',
 brandColor: '#E4CA00',
 expectedPerCreator: 1800,
 suggestionsEnabled: false,
 suggestions: [],
 includedCampaigns: [],
 amplificationCard: {
 enabled: false,
 originalTweet: {
 authorHandle: '', authorName: '', content: '', tweetUrl: '',
 beforeViews: 0, beforeReplies: 0, beforeRetweets: 0, beforeLikes: 0
 },
 beforeTweet: { authorHandle: '', authorName: '', content: '' }
 }
};

const hydrateConfig = (data = {}) => ({
 ...DEFAULT_CONFIG,
 ...data,
 includedCampaigns: data.includedCampaigns || [],
 suggestionsEnabled: data.suggestionsEnabled || false,
 suggestions: data.suggestions || [],
 amplificationCard: {
 ...DEFAULT_CONFIG.amplificationCard,
 ...(data.amplificationCard || {}),
 originalTweet: {
 ...DEFAULT_CONFIG.amplificationCard.originalTweet,
 ...(data.amplificationCard?.originalTweet || {})
 },
 beforeTweet: {
 ...DEFAULT_CONFIG.amplificationCard.beforeTweet,
 ...(data.amplificationCard?.beforeTweet || {})
 }
 }
});

const groupFields = (config = {}) => ({
 enabled: Boolean(config.enabled),
 slug: config.slug || '',
 accessCode: config.accessCode || ''
});

const sameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const Section = ({ title, description, children }) => {
 const id = `report-${title.toLowerCase().replace(/\s+/g, '-')}`;
 return (
 <section className="border border-glass-border bg-surface-1" aria-labelledby={id}>
 <div className="border-b border-glass-border px-4 py-3 sm:px-5">
 <h4 id={id} className="text-base font-semibold text-text-primary">{title}</h4>
 {description && <p className="mt-1 text-sm text-text-secondary">{description}</p>}
 </div>
 <div className="flex flex-col gap-5 p-4 sm:p-5">{children}</div>
 </section>
 );
};

const PublicReportEditor = ({ campaignId, compact = false, onDirtyChange, onSavingChange }) => {
 const [config, setConfig] = useState(DEFAULT_CONFIG);
 const [savedConfig, setSavedConfig] = useState(DEFAULT_CONFIG);
 const [childCampaigns, setChildCampaigns] = useState([]);
 const [loading, setLoading] = useState(true);
 const [loadError, setLoadError] = useState('');
 const [saving, setSaving] = useState(false);
 const [saveError, setSaveError] = useState('');
 const [syncingPfp, setSyncingPfp] = useState(false);
 const [brandTwitter, setBrandTwitter] = useState('');
 const [accordionOpen, setAccordionOpen] = useState(false);
 const [activeSection, setActiveSection] = useState('access');
 const [externalCreators, setExternalCreators] = useState([]);
 const [externalCreatorsLoading, setExternalCreatorsLoading] = useState(true);
 const [externalCreatorsError, setExternalCreatorsError] = useState('');
 const [uploading, setUploading] = useState(false);
 const [uploadResult, setUploadResult] = useState(null);
 const [syncing, setSyncing] = useState(false);
 const [syncJobId, setSyncJobId] = useState(null);
 const [removingCreatorId, setRemovingCreatorId] = useState(null);
 const [groups, setGroups] = useState([]);
 const [groupReports, setGroupReports] = useState({});
 const [savedGroupReports, setSavedGroupReports] = useState({});
 const [groupListLoadError, setGroupListLoadError] = useState('');
 const fileInputRef = useRef(null);
 const writeInFlightRef = useRef(false);
 const syncRequestRef = useRef(0);
 const syncAbortRef = useRef(null);
 const currentCampaignRef = useRef(campaignId);
 currentCampaignRef.current = campaignId;

 useEffect(() => {
 const syncRequest = syncRequestRef;
 const syncAbort = syncAbortRef;
 setSyncJobId(null);
 setSyncing(false);
 writeInFlightRef.current = false;
 return () => {
 syncRequest.current++;
 syncAbort.current?.abort();
 };
 }, [campaignId]);

 const fetchConfig = useCallback(async () => {
 setLoading(true);
 setLoadError('');
 try {
 const { data } = await apiClient.get(`/campaigns/${campaignId}/public-report`);
 const autoSlug = !data?.slug && data?.campaignTitle
 ? data.campaignTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').substring(0, 60)
 : undefined;
 const persistedConfig = hydrateConfig(data);
 const nextConfig = hydrateConfig({ ...data, ...(autoSlug ? { slug: autoSlug } : {}) });
 setChildCampaigns(data?.childCampaigns || []);
 setBrandTwitter(data?.brandTwitter || '');
 setConfig(nextConfig);
 setSavedConfig(persistedConfig);
 } catch (error) {
 if (error.response?.status === 404) {
 const nextConfig = hydrateConfig();
 setConfig(nextConfig);
 setSavedConfig(nextConfig);
 } else {
 setLoadError(error.response?.data?.error || 'Failed to load public report settings');
 }
 } finally {
 setLoading(false);
 }
 }, [campaignId]);

 const fetchExternalCreators = useCallback(async () => {
 const requestId = syncRequestRef.current;
 setExternalCreatorsLoading(true);
 setExternalCreatorsError('');
 try {
 const { data } = await apiClient.get(`/campaigns/${campaignId}/external-creators`);
 if (requestId !== syncRequestRef.current || currentCampaignRef.current !== campaignId) return;
 setExternalCreators(data.creators || []);
 } catch (error) {
 if (requestId !== syncRequestRef.current || currentCampaignRef.current !== campaignId) return;
 setExternalCreatorsError(error.response?.data?.error || 'External creators could not be loaded');
 } finally {
 if (requestId === syncRequestRef.current && currentCampaignRef.current === campaignId) setExternalCreatorsLoading(false);
 }
 }, [campaignId]);

 const fetchGroups = useCallback(async () => {
 setGroupListLoadError('');
 try {
 const { data } = await apiClient.get(`/campaigns/${campaignId}/groups`);
 const groupList = data.groups || [];
 if (groupList.some(group => !Object.prototype.hasOwnProperty.call(group, 'publicReport'))) {
 throw new Error('Group report settings are missing from the response');
 }
 const reports = {};
 groupList.forEach(group => { reports[group._id] = groupFields(group.publicReport); });
 setGroups(groupList);
 setGroupReports(reports);
 setSavedGroupReports(reports);
 } catch (error) {
 setGroupListLoadError(
 error.response?.data?.error || error.message || 'Group reports could not be loaded'
 );
 }
 }, [campaignId]);

 const loadEditor = useCallback(() => {
 fetchConfig();
 fetchExternalCreators();
 fetchGroups();
 }, [fetchConfig, fetchExternalCreators, fetchGroups]);

 useEffect(() => { loadEditor(); }, [loadEditor]);

 const configDirty = useMemo(() => !sameValue(config, savedConfig), [config, savedConfig]);
 const dirtyGroupIds = useMemo(() => groups
 .map(group => group._id)
 .filter(groupId => groupReports[groupId] && savedGroupReports[groupId]
 && !sameValue(groupFields(groupReports[groupId]), groupFields(savedGroupReports[groupId]))),
 [groupReports, groups, savedGroupReports]);
 const isDirty = configDirty || dirtyGroupIds.length > 0;
 const writeInFlight = saving || syncingPfp || uploading || syncing || Boolean(removingCreatorId);

 const discardChanges = useCallback(() => {
 setConfig(savedConfig);
 setGroupReports(savedGroupReports);
 setSaveError('');
 }, [savedConfig, savedGroupReports]);
 const {
 setDirty: setReportDirty,
 setInFlight: setReportInFlight,
 } = useDirtyNavigationGuard(`public-report-${campaignId}`, { onDiscard: discardChanges });

 useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);
 useEffect(() => { onSavingChange?.(writeInFlight); }, [onSavingChange, writeInFlight]);
 useEffect(() => { setReportDirty(isDirty); }, [isDirty, setReportDirty]);
 useEffect(() => { setReportInFlight(writeInFlight); }, [setReportInFlight, writeInFlight]);

 const reportUrl = buildReportUrl(config.slug, config.canonicalUrl);
 const savedReportUrl = buildReportUrl(savedConfig.slug, savedConfig.canonicalUrl);
 const publicationReady = Boolean(config.slug?.trim() && config.accessCode?.trim());
 const reportCanOpenAfterSave = Boolean(config.enabled && publicationReady && reportUrl);
 const savedPublicationReady = Boolean(savedConfig.slug?.trim() && savedConfig.accessCode?.trim());
 const savedReportCanOpen = Boolean(savedConfig.enabled && savedPublicationReady && savedReportUrl);
 const invalidGroup = groups.find(group => {
 const report = groupFields(groupReports[group._id]);
 return report.enabled && (!report.slug.trim() || !report.accessCode.trim());
 });

 const saveChanges = async ({ openAfterSave = false } = {}) => {
 if (config.enabled && !publicationReady) {
 const message = 'Add an access code and URL slug before turning the report live';
 setActiveSection('access');
 setSaveError(message);
 toast.error(message);
 return false;
 }
 if (invalidGroup) {
 const message = `Add an access code and URL slug before turning ${invalidGroup.name} live`;
 setActiveSection('included');
 setSaveError(message);
 toast.error(message);
 return false;
 }
 if (writeInFlightRef.current) return false;
 writeInFlightRef.current = true;
 const pendingReportWindow = openAfterSave ? window.open('about:blank', '_blank') : null;
 if (pendingReportWindow) pendingReportWindow.opener = null;
 setSaveError('');
 setSaving(true);
 let committedReportUrl = reportUrl;
 let committedReportCanOpen = reportCanOpenAfterSave;
 let parentSaved = false;
 try {
 if (configDirty) {
 const { data } = await apiClient.put(`/campaigns/${campaignId}/public-report`, config);
 const committedConfig = hydrateConfig({ ...config, ...(data || {}) });
 setConfig(committedConfig);
 setSavedConfig(committedConfig);
 parentSaved = true;
 committedReportUrl = buildReportUrl(committedConfig.slug, committedConfig.canonicalUrl);
 committedReportCanOpen = Boolean(
 committedConfig.enabled
 && committedConfig.slug?.trim()
 && committedConfig.accessCode?.trim()
 && committedReportUrl
 );
 }
 if (dirtyGroupIds.length > 0) {
 await apiClient.put(`/campaigns/${campaignId}/groups/public-report/batch`, {
 groups: dirtyGroupIds.map(groupId => ({
 groupId,
 ...groupFields(groupReports[groupId])
 }))
 });
 setSavedGroupReports(groupReports);
 }
 toast.success(isDirty ? 'Public report changes saved' : 'Public report is already up to date');
 if (openAfterSave) {
 if (committedReportCanOpen && pendingReportWindow) pendingReportWindow.location.assign(committedReportUrl);
 else if (committedReportCanOpen) window.open(committedReportUrl, '_blank', 'noopener,noreferrer');
 else {
 pendingReportWindow?.close();
 toast.error('Turn the report live and set a URL slug before opening it');
 }
 }
 return true;
 } catch (error) {
 pendingReportWindow?.close();
 const detail = error.response?.data?.error || 'Failed to save public report changes';
 const message = parentSaved
 ? `Report changes were saved, but group report changes were not: ${detail}`
 : detail;
 setSaveError(parentSaved
 ? `${message}. Your group report changes are still here.`
 : `${message}. Your unsaved changes are still here.`);
 toast.error(message);
 return false;
 } finally {
 writeInFlightRef.current = false;
 setSaving(false);
 }
 };

 const handleFieldChange = (field, value) => setConfig(previous => ({ ...previous, [field]: value }));

 const generateAccessCode = () => {
 const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
 let code = '';
 for (let index = 0; index < 6; index += 1) code += chars.charAt(Math.floor(Math.random() * chars.length));
 handleFieldChange('accessCode', code);
 };

 const handleToggleChildCampaign = (child) => setConfig(previous => {
 const included = previous.includedCampaigns || [];
 const exists = included.some(item => item.campaignId === child._id);
 return {
 ...previous,
 includedCampaigns: exists
 ? included.filter(item => item.campaignId !== child._id)
 : [...included, { campaignId: child._id, name: child.name }]
 };
 });

 const handleAmplificationToggle = (enabled) => setConfig(previous => ({
 ...previous, amplificationCard: { ...previous.amplificationCard, enabled }
 }));

 const handleAmplificationChange = (field, value) => setConfig(previous => ({
 ...previous,
 amplificationCard: {
 ...previous.amplificationCard,
 originalTweet: { ...previous.amplificationCard?.originalTweet, [field]: value }
 }
 }));

 const handleBeforeTweetChange = (field, value) => setConfig(previous => ({
 ...previous,
 amplificationCard: {
 ...previous.amplificationCard,
 beforeTweet: { ...previous.amplificationCard?.beforeTweet, [field]: value }
 }
 }));

 const handleSuggestionChange = (index, field, value) => setConfig(previous => {
 const suggestions = [...previous.suggestions];
 suggestions[index] = { ...suggestions[index], [field]: value };
 return { ...previous, suggestions };
 });

 const handleSyncTwitterPfp = async () => {
 if (!brandTwitter.trim()) {
 toast.error('Enter an X handle first');
 return;
 }
 if (writeInFlightRef.current) return;
 writeInFlightRef.current = true;
 setSyncingPfp(true);
 try {
 const { data } = await apiClient.post(`/campaigns/${campaignId}/public-report/sync-twitter-pfp`, {
 twitterHandle: brandTwitter.trim()
 });
 if (data.profileLookupSucceeded !== true) {
 throw new Error(data.message || 'The X profile update could not be confirmed');
 }
 setConfig(previous => ({ ...previous, brandIconUrl: data.brandIconUrl ?? previous.brandIconUrl }));
 setSavedConfig(previous => ({ ...previous, brandIconUrl: data.brandIconUrl ?? previous.brandIconUrl }));
 setBrandTwitter(data.twitter || brandTwitter);
 toast.success(data.profileImageUpdated === true ? 'Brand profile updated from X' : (data.message || 'X handle saved; no profile image was found'));
 } catch (error) {
 toast.error(error.response?.data?.error || error.message || 'Failed to update brand profile from X');
 } finally {
 writeInFlightRef.current = false;
 setSyncingPfp(false);
 }
 };

 const handleFileUpload = async (event) => {
 const file = event.target.files?.[0];
 if (!file) return;
 if (writeInFlightRef.current) return;
 writeInFlightRef.current = true;
 setUploading(true);
 setUploadResult(null);
 try {
 const formData = new FormData();
 formData.append('file', file);
 const { data } = await apiClient.post(`/campaigns/${campaignId}/external-creators/upload`, formData, {
 headers: { 'Content-Type': 'multipart/form-data' }
 });
 setUploadResult(data);
 toast.success(`Added ${data.added} creators (${data.duplicates} duplicates, ${data.errors} errors)`);
 await fetchExternalCreators();
 } catch (error) {
 toast.error(error.response?.data?.error || 'Upload failed');
 } finally {
 writeInFlightRef.current = false;
 setUploading(false);
 if (fileInputRef.current) fileInputRef.current.value = '';
 }
 };

 const handleSyncImpressions = async () => {
 if (writeInFlightRef.current) return;
 writeInFlightRef.current = true;
 setSyncing(true);
 const requestId = ++syncRequestRef.current;
 const controller = new AbortController();
 syncAbortRef.current = controller;
 const isCurrent = () => requestId === syncRequestRef.current && currentCampaignRef.current === campaignId;
 try {
 let data = syncJobId
 ? { jobId: syncJobId, status: 'queued' }
 : (await apiClient.post(`/campaigns/${campaignId}/external-creators/sync`)).data;
 if (!isCurrent()) return;
 if (data.jobId) {
 const jobId = data.jobId;
 setSyncJobId(jobId);
 const deadline = Date.now() + 4 * 60 * 1000;
 let attempts = 0;
 while (['queued', 'running'].includes(data.status)) {
 data = (await apiClient.get(`/campaigns/${campaignId}/external-creators/sync/${encodeURIComponent(jobId)}`, {
 signal: controller.signal, timeout: 10000
 })).data;
 if (!isCurrent()) return;
 attempts++;
 if (['queued', 'running'].includes(data.status)) {
 if (attempts >= 120 || Date.now() >= deadline) {
 toast.error('Impression sync is still running in the background. Use Check sync status to check progress.');
 return;
 }
 await new Promise(resolve => setTimeout(resolve, 2000));
 if (!isCurrent()) return;
 }
 }
 setSyncJobId(null);
 if (data.status === 'failed') {
 toast.error(data.error || 'Impression sync failed. Existing metrics were retained.');
 await fetchExternalCreators();
 return;
 }
 if (data.status !== 'completed') throw new Error('The impression sync result could not be confirmed');
 }

 const synced = Number.isFinite(data.synced) && data.synced >= 0 ? data.synced : null;
 const total = Number.isFinite(data.total) && data.total >= 0 ? data.total : (synced === 0 && !data.errors?.length ? 0 : null);
 const failed = Math.max(Number.isFinite(data.failed) ? data.failed : 0, total === null ? 0 : total - synced);
 if (failed > 0 || data.errors?.length) {
 toast.error(synced > 0
 ? `Synced ${synced} of ${total ?? 'unknown'} creators; ${failed || 'some'} updates failed`
 : 'No impressions updated; sync failed. Existing metrics were retained.');
 } else if (total === 0 && synced === 0) {
 toast.success(data.message || 'No external creator tweets to sync');
 } else if (total !== null && synced === total) {
 toast.success(`Synced ${synced} of ${total} creators`);
 } else {
 toast.error('The impression sync result could not be confirmed');
 }
 await fetchExternalCreators();
 } catch (error) {
 if (!isCurrent()) return;
 if (error.response?.status === 404) setSyncJobId(null);
 toast.error(error.response?.data?.error || error.message || 'Sync failed');
 } finally {
 if (isCurrent()) {
 writeInFlightRef.current = false;
 setSyncing(false);
 }
 }
 };

 const handleRemoveExternalCreator = async (creator) => {
 if (writeInFlightRef.current) return;
 const name = creator.name || creator.twitterHandle || 'this creator';
 if (!window.confirm(`Remove ${name} from this campaign now? This action is immediate.`)) return;
 writeInFlightRef.current = true;
 setRemovingCreatorId(creator.creatorId);
 try {
 await apiClient.delete(`/campaigns/${campaignId}/external-creators/${creator.creatorId}`);
 setExternalCreators(previous => previous.filter(item => item.creatorId !== creator.creatorId));
 toast.success('External creator removed');
 } catch (error) {
 toast.error(error.response?.data?.error || 'Failed to remove creator');
 } finally {
 writeInFlightRef.current = false;
 setRemovingCreatorId(null);
 }
 };

 const handleDownloadTemplate = () => {
 const blob = new Blob(['url,name\nhttps://x.com/username/status/1234567890,Creator Name\n'], { type: 'text/csv' });
 const url = URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = url;
 link.download = 'external-creators-template.csv';
 link.click();
 URL.revokeObjectURL(url);
 };

 const handleGroupReportChange = (groupId, field, value) => setGroupReports(previous => ({
 ...previous, [groupId]: { ...groupFields(previous[groupId]), [field]: value }
 }));

 const generateGroupAccessCode = (groupId) => {
 const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
 let code = '';
 for (let index = 0; index < 6; index += 1) code += chars.charAt(Math.floor(Math.random() * chars.length));
 handleGroupReportChange(groupId, 'accessCode', code);
 };

 const copyUrl = async (url) => {
 if (!url) return;
 try {
 await navigator.clipboard.writeText(url);
 toast.success('Report URL copied');
 } catch (_) {
 toast.error('Could not copy the report URL');
 }
 };

 if (loading) {
 return <div className={`${compact ? '' : 'p-6 mb-6'} flex min-h-[120px] items-center justify-center`}><Spinner size="sm" className="mr-3" /><span className="text-sm text-text-secondary">Loading public report settings...</span></div>;
 }

 if (loadError) {
 return <Alert variant="destructive" className={compact ? '' : 'mb-6'}><AlertDescription className="flex flex-wrap items-center justify-between gap-3"><span>{loadError}</span><Button variant="outline" size="sm" onClick={loadEditor}>Retry</Button></AlertDescription></Alert>;
 }

 const statusColor = (status) => {
 if (String(status).toUpperCase() === 'ACTIVE') return 'text-success border-success/30 bg-success-muted';
 if (String(status).toUpperCase() === 'COMPLETED') return 'text-info border-info/30 bg-info-muted';
 return 'text-text-secondary border-glass-border bg-glass-bg';
 };

 const formContent = (
 <fieldset disabled={writeInFlight} aria-busy={writeInFlight} className="min-w-0 border-0 p-0">
 <div className="flex flex-col gap-5">
 <div className="border border-glass-border bg-surface-1 p-4">
 <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
 <div className="min-w-0 flex-1">
 <div className="flex flex-wrap items-center gap-2">
 <span className={`inline-flex min-w-[52px] justify-center border px-2 py-1 text-xs font-semibold ${savedConfig.enabled ? 'border-success/30 bg-success-muted text-success' : 'border-glass-border bg-surface-2 text-text-secondary'}`}>{savedConfig.enabled ? 'LIVE' : 'OFF'}</span>
 <span className={`border px-2 py-1 text-xs font-medium ${publicationReady ? 'border-success/30 text-success' : 'border-warning/30 text-warning'}`}>{publicationReady ? 'Ready' : 'Needs setup'}</span>
 {isDirty && <span className="text-xs font-medium text-warning">Unsaved changes</span>}
 </div>
 <p className="mt-2 truncate text-sm text-text-secondary">{reportUrl || 'Add an access code and URL in Access to prepare the report.'}</p>
 {config.lastPublishedAt && <p className="mt-1 text-xs text-text-tertiary">Last published {new Date(config.lastPublishedAt).toLocaleString()}</p>}
 </div>
 <div className="flex flex-col gap-2 sm:flex-row">
 <Button type="button" variant="outline" onClick={() => copyUrl(reportUrl)} disabled={!reportUrl}><IconCopy size={16} className="mr-2" />{config.enabled ? 'Copy link' : 'Copy configured link'}</Button>
 <Button type="button" variant="outline" onClick={() => window.open(savedReportUrl, '_blank', 'noopener,noreferrer')} disabled={!savedReportCanOpen}><IconExternalLink size={16} className="mr-2" />Open published report</Button>
 </div>
 </div>
 </div>

 <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
 <nav className="grid grid-cols-2 gap-2 self-start lg:sticky lg:top-0 lg:grid-cols-1" aria-label="Report editor sections">
 {REPORT_SECTIONS.map((section, index) => {
 const selected = activeSection === section.id;
 return (
 <button
 key={section.id}
 type="button"
 onClick={() => setActiveSection(section.id)}
 aria-current={selected ? 'step' : undefined}
 aria-controls={`report-panel-${section.id}`}
 className={`min-h-[52px] border px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${selected ? 'border-accent bg-accent/10' : 'border-glass-border bg-surface-1 hover:bg-glass-bg-hover'}`}
 >
 <span className="flex items-center gap-2 text-sm font-semibold text-text-primary"><span className={`flex h-5 w-5 items-center justify-center border text-[11px] ${selected ? 'border-accent bg-accent text-accent-foreground' : 'border-glass-border text-text-secondary'}`}>{index + 1}</span>{section.label}</span>
 <span className="mt-1 hidden text-xs leading-snug text-text-tertiary lg:block">{section.description}</span>
 </button>
 );
 })}
 </nav>

 <div id={`report-panel-${activeSection}`} className="min-w-0" tabIndex="-1">
 {activeSection === 'access' && (
 <Section title="Access" description="Control whether the report is live and how viewers open it.">
 <div className="flex flex-col gap-4 border border-glass-border bg-glass-bg p-4 sm:flex-row sm:items-center sm:justify-between">
 <div className="flex items-center gap-3">
 <span className={`inline-flex min-w-[52px] justify-center border px-2 py-1 text-xs font-semibold ${config.enabled ? 'border-success/30 bg-success-muted text-success' : 'border-glass-border bg-surface-2 text-text-secondary'}`}>{config.enabled ? 'LIVE' : 'OFF'}</span>
 <div><p className="text-sm font-medium text-text-primary">Public report</p><p className="text-xs text-text-secondary">{config.enabled ? 'Viewers can open it with the access code.' : 'Only editors can configure it.'}</p></div>
 </div>
 <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-3"><input type="checkbox" checked={config.enabled} onChange={event => handleFieldChange('enabled', event.target.checked)} className="h-5 w-5 border-glass-border text-accent focus:ring-accent" /><span className="text-sm font-medium text-text-primary">{config.enabled ? 'Report is live' : 'Turn report live'}</span></label>
 </div>
 <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
 <div className="space-y-1"><Label htmlFor="accessCode">Access code</Label><div className="flex gap-2"><Input id="accessCode" value={config.accessCode} onChange={event => handleFieldChange('accessCode', event.target.value.toUpperCase())} maxLength={100} className="uppercase font-mono tracking-widest" /><Button type="button" variant="outline" onClick={generateAccessCode} aria-label="Generate access code"><IconRefresh size={18} /></Button></div><p className="text-xs text-text-tertiary">Required when the report is live.</p></div>
 <div className="space-y-1"><Label htmlFor="slug">URL slug</Label><Input id="slug" value={config.slug} onChange={event => handleFieldChange('slug', event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} /><p className="text-xs text-text-tertiary">{config.slug ? reportUrl : 'Set a slug to create the public URL.'}</p></div>
 </div>
 {config.enabled && !publicationReady && <Alert variant="warning"><AlertDescription>Before this report can go live, add {config.accessCode?.trim() ? 'a URL slug' : config.slug?.trim() ? 'an access code' : 'an access code and URL slug'}.</AlertDescription></Alert>}
 </Section>
 )}

 {activeSection === 'content' && (
 <Section title="Content" description="Edit the campaign information and messages shown to viewers.">
 {config.brandType === 'partner' && config.whitelabelConfig && <Alert variant="info"><AlertDescription>This report uses <strong>{config.whitelabelConfig.companyName || config.brandName}</strong> partner branding.</AlertDescription></Alert>}
 <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
 <div className="space-y-1"><Label htmlFor="campaignTitle">Campaign title</Label><Input id="campaignTitle" value={config.campaignTitle} onChange={event => handleFieldChange('campaignTitle', event.target.value)} /></div>
 <div className="space-y-1"><Label htmlFor="clientName">Client name</Label><Input id="clientName" value={config.clientName} onChange={event => handleFieldChange('clientName', event.target.value)} />{config.brandName && <p className="text-xs text-text-tertiary">Brand: {config.brandName}</p>}</div>
 <div className="space-y-1"><Label htmlFor="campaignTypeLabel">Campaign type label</Label><Input id="campaignTypeLabel" value={config.campaignTypeLabel} onChange={event => handleFieldChange('campaignTypeLabel', event.target.value)} /></div>
 <div className="space-y-1"><Label htmlFor="reportDate">Report date</Label><Input id="reportDate" type="date" value={toDateInput(config.reportDate)} onChange={event => handleFieldChange('reportDate', fromDateInput(event.target.value))} /></div>
 </div>
 <div className="space-y-1"><Label htmlFor="heroMessage">Hero message (completed)</Label><textarea id="heroMessage" value={config.heroMessage} onChange={event => handleFieldChange('heroMessage', event.target.value)} rows={2} placeholder="Message shown when the campaign is complete" className="flex w-full resize-y border border-glass-border bg-glass-bg px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" /></div>
 <div className="space-y-1"><Label htmlFor="heroMessageInProgress">Hero message (in progress)</Label><textarea id="heroMessageInProgress" value={config.heroMessageInProgress} onChange={event => handleFieldChange('heroMessageInProgress', event.target.value)} rows={2} placeholder="Message shown while the campaign is in progress" className="flex w-full resize-y border border-glass-border bg-glass-bg px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" /></div>
 <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-3"><input type="checkbox" checked={config.suggestionsEnabled} onChange={event => handleFieldChange('suggestionsEnabled', event.target.checked)} className="h-5 w-5 border-glass-border text-accent focus:ring-accent" /><span className="text-sm font-medium text-text-primary">Show suggestions</span></label>
 {config.suggestionsEnabled && <div className="flex flex-col gap-3">
 {config.suggestions.map((suggestion, index) => <div key={index} className="border border-glass-border p-4"><div className="mb-3 flex items-center justify-between"><span className="text-sm font-semibold text-text-secondary">Suggestion {index + 1}</span><Button type="button" variant="ghost" size="icon" onClick={() => setConfig(previous => ({ ...previous, suggestions: previous.suggestions.filter((_, itemIndex) => itemIndex !== index) }))} aria-label={`Remove suggestion ${index + 1}`} className="text-error"><IconTrash size={16} /></Button></div><div className="grid grid-cols-1 gap-4 md:grid-cols-3"><div className="space-y-1"><Label htmlFor={`suggestion-${index}-title`}>Title</Label><Input id={`suggestion-${index}-title`} value={suggestion.title} onChange={event => handleSuggestionChange(index, 'title', event.target.value)} /></div><div className="space-y-1 md:col-span-2"><Label htmlFor={`suggestion-${index}-description`}>Description</Label><Input id={`suggestion-${index}-description`} value={suggestion.description} onChange={event => handleSuggestionChange(index, 'description', event.target.value)} /></div></div></div>)}
 <Button type="button" variant="outline" size="sm" onClick={() => setConfig(previous => ({ ...previous, suggestions: [...previous.suggestions, { title: '', description: '' }] }))} className="self-start"><IconCirclePlus size={16} className="mr-2" />Add suggestion</Button>
 </div>}
 </Section>
 )}

 {activeSection === 'included' && (
 <Section title="Included data" description="Choose campaign data and manage report-specific creator and group content.">
 {childCampaigns.length > 0 ? <div><h5 className="mb-1 text-sm font-semibold text-text-primary">Included campaigns</h5><p className="mb-3 text-sm text-text-secondary">Their groups will appear in the public report.</p><div className="overflow-hidden border border-glass-border">
 {childCampaigns.map((child, index) => {
 const checked = config.includedCampaigns.some(item => item.campaignId === child._id);
 return <label key={child._id} className={`flex min-h-[48px] cursor-pointer items-center gap-3 px-4 py-3 hover:bg-glass-bg-hover ${index < childCampaigns.length - 1 ? 'border-b border-glass-border' : ''}`} style={{ paddingLeft: `${16 + (child.depth || 0) * 24}px` }}><input type="checkbox" checked={checked} onChange={() => handleToggleChildCampaign(child)} className="h-5 w-5 shrink-0 border-glass-border text-accent focus:ring-accent" /><span className="min-w-0 flex-1"><span className="block truncate text-sm text-text-primary">{child.name}</span><span className="text-xs text-text-tertiary">{child.isParent ? 'Parent campaign' : `Part ${(child.ordinal ?? 0) + 1}`}</span></span><span className={`border px-2 py-0.5 text-xs capitalize ${statusColor(child.status)}`}>{child.status}</span></label>;
 })}
 </div></div> : <p className="text-sm text-text-tertiary">No child campaigns are available.</p>}

 <div className="border-t border-glass-border pt-5"><div className="flex flex-wrap items-center gap-2"><h5 className="text-sm font-semibold text-text-primary">External creators</h5><span className="border border-warning/30 px-2 py-0.5 text-xs font-medium text-warning">Updates now</span></div><p className="mt-1 text-sm text-text-secondary">These actions update campaign data immediately; they are not waiting for Save changes.</p>
 <div className="mt-3 flex flex-wrap items-center gap-2"><input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" aria-label="External creator file" /><Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading || externalCreatorsLoading}>{uploading ? <Spinner size="sm" className="mr-2" /> : <IconUpload size={16} className="mr-2" />}{uploading ? 'Uploading...' : 'Upload creators now'}</Button><Button type="button" variant="ghost" size="sm" onClick={handleDownloadTemplate}><IconDownload size={16} className="mr-2" />Download template</Button>{externalCreators.length > 0 && <Button type="button" variant="outline" size="sm" onClick={handleSyncImpressions} disabled={syncing}>{syncing ? <Spinner size="sm" className="mr-2" /> : <IconRefresh size={16} className="mr-2" />}{syncing ? 'Syncing...' : syncJobId ? 'Check sync status' : 'Sync impressions now'}</Button>}</div>
 {uploadResult && <Alert variant={uploadResult.errors > 0 ? 'warning' : 'info'} className="mt-3"><AlertDescription>Added {uploadResult.added} creators ({uploadResult.creatorsCreated} new), {uploadResult.duplicates} duplicates, {uploadResult.errors} errors</AlertDescription></Alert>}
 {externalCreatorsLoading ? <div className="mt-3 flex min-h-[72px] items-center justify-center border border-glass-border" role="status"><Spinner size="sm" className="mr-2" /><span className="text-sm text-text-secondary">Loading external creators...</span></div> : externalCreatorsError ? <div className="mt-3 flex flex-col gap-3 border border-error/30 bg-error-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between" role="alert"><p className="text-sm text-error">{externalCreatorsError}. Existing creator data has not been replaced.</p><Button type="button" variant="outline" size="sm" onClick={fetchExternalCreators}>Retry external creators</Button></div> : externalCreators.length > 0 ? <div className="mt-3 overflow-x-auto border border-glass-border"><table className="w-full text-sm"><thead><tr className="border-b border-glass-border bg-glass-bg"><th className="px-3 py-2 text-left font-medium text-text-secondary">Creator</th><th className="px-3 py-2 text-left font-medium text-text-secondary">Post</th><th className="px-3 py-2 text-right font-medium text-text-secondary">Impressions</th><th className="px-3 py-2 text-right font-medium text-text-secondary">Likes</th><th className="px-3 py-2 text-right font-medium text-text-secondary">RTs</th><th className="px-3 py-2"><span className="sr-only">Actions</span></th></tr></thead><tbody>
 {externalCreators.map((creator, index) => <tr key={creator.creatorId || index} className="border-b border-glass-border last:border-b-0 hover:bg-glass-bg-hover"><td className="px-3 py-2 text-text-primary"><span className="font-medium">{creator.name || creator.twitterHandle || 'Unknown'}</span>{creator.twitterHandle && <span className="ml-1 text-text-tertiary">@{creator.twitterHandle}</span>}</td><td className="px-3 py-2">{creator.tweetUrl && <a href={creator.tweetUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline"><IconLink size={14} /><span className="inline-block max-w-[120px] truncate align-bottom">{creator.tweetId || 'View'}</span></a>}</td><td className="px-3 py-2 text-right font-mono text-text-primary">{(creator.impressions || 0).toLocaleString()}</td><td className="px-3 py-2 text-right font-mono text-text-primary">{(creator.likes || 0).toLocaleString()}</td><td className="px-3 py-2 text-right font-mono text-text-primary">{(creator.retweets || 0).toLocaleString()}</td><td className="px-3 py-2 text-right"><Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveExternalCreator(creator)} aria-label={`Remove ${creator.name || creator.twitterHandle || 'external creator'} now`} className="text-error"><IconX size={16} /></Button></td></tr>)}
 </tbody></table></div> : <p className="mt-3 text-sm italic text-text-tertiary">No external creators added yet.</p>}</div>

 {groupListLoadError && <div className="border-t border-glass-border pt-5"><div className="flex flex-col gap-3 border border-error/30 bg-error-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between" role="alert"><div><h5 className="text-sm font-semibold text-text-primary">Group reports unavailable</h5><p className="mt-1 text-sm text-error">{groupListLoadError}. Existing group settings have not been replaced.</p></div><Button type="button" variant="outline" size="sm" onClick={fetchGroups}>Retry group reports</Button></div></div>}

 {!groupListLoadError && groups.length > 0 && <div className="border-t border-glass-border pt-5"><h5 className="text-sm font-semibold text-text-primary">Group reports</h5><p className="mt-1 text-sm text-text-secondary">Group changes are saved together with the primary Save changes action.</p><div className="mt-3 flex flex-col gap-3">
 {groups.map(group => {
 const groupReport = groupFields(groupReports[group._id]);
 const groupUrl = buildReportUrl(groupReport.slug, config.canonicalUrl);
 const savedGroupReport = groupFields(savedGroupReports[group._id]);
 const savedGroupUrl = buildReportUrl(savedGroupReport.slug, savedConfig.canonicalUrl);
 const groupDirty = dirtyGroupIds.includes(group._id);
 const groupCanOpen = Boolean(
 savedGroupReport.enabled
 && savedGroupReport.slug.trim()
 && savedGroupReport.accessCode.trim()
 && savedGroupUrl
 );
 return <div key={group._id} className="border border-glass-border p-4"><div className="mb-3 flex flex-wrap items-center gap-3"><label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2"><input type="checkbox" checked={groupReport.enabled} onChange={event => handleGroupReportChange(group._id, 'enabled', event.target.checked)} className="h-5 w-5 border-glass-border text-accent focus:ring-accent" /><span className="text-sm font-semibold text-text-primary">{group.name}</span></label><span className={`border px-2 py-0.5 text-xs capitalize ${statusColor(group.status)}`}>{group.status}</span>{groupDirty && <span className="text-xs font-medium text-warning">Unsaved</span>}</div><div className="grid grid-cols-1 gap-3 md:grid-cols-3"><div className="space-y-1"><Label htmlFor={`group-${group._id}-slug`}>Slug</Label><Input id={`group-${group._id}-slug`} value={groupReport.slug} onChange={event => handleGroupReportChange(group._id, 'slug', event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} /></div><div className="space-y-1"><Label htmlFor={`group-${group._id}-code`}>Access code</Label><div className="flex gap-2"><Input id={`group-${group._id}-code`} value={groupReport.accessCode} onChange={event => handleGroupReportChange(group._id, 'accessCode', event.target.value.toUpperCase())} maxLength={100} className="uppercase font-mono tracking-widest" /><Button type="button" variant="outline" onClick={() => generateGroupAccessCode(group._id)} aria-label={`Generate access code for ${group.name}`}><IconRefresh size={16} /></Button></div></div><div className="space-y-1"><Label>URL</Label><div className="flex min-h-[40px] items-center gap-1"><span className="min-w-0 flex-1 truncate text-xs text-text-tertiary">{groupUrl || 'Set a slug'}</span><Button type="button" variant="ghost" size="icon" onClick={() => copyUrl(groupUrl)} disabled={!groupUrl} aria-label={`Copy URL for ${group.name}`}><IconCopy size={16} /></Button><Button type="button" variant="ghost" size="icon" onClick={() => window.open(savedGroupUrl, '_blank', 'noopener,noreferrer')} disabled={!groupCanOpen} aria-label={`Open report for ${group.name}`}><IconExternalLink size={16} /></Button></div></div></div></div>;
 })}
 </div></div>}
 </Section>
 )}

 {activeSection === 'advanced' && (
 <Section title="Advanced" description="Brand presentation, expected performance, and amplification comparison.">
 <div className="border border-warning/30 p-4"><div className="flex flex-wrap items-center gap-2"><h5 className="text-sm font-semibold text-text-primary">Brand profile</h5><span className="border border-warning/30 px-2 py-0.5 text-xs font-medium text-warning">Updates now</span></div><p className="mt-1 text-sm text-text-secondary">This immediate action updates the shared brand handle and image now; it is separate from Save changes.</p><div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">{config.brandIconUrl && <img src={config.brandIconUrl} alt={`${config.brandName || 'Brand'} profile`} className="h-11 w-11 shrink-0 object-cover" />}<div className="min-w-0 flex-1 space-y-1"><Label htmlFor="brandTwitter">X handle</Label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-tertiary">@</span><Input id="brandTwitter" value={brandTwitter} onChange={event => setBrandTwitter(event.target.value.replace(/^@/, ''))} placeholder="brandhandle" className="pl-7" /></div></div><Button type="button" variant="outline" onClick={handleSyncTwitterPfp} disabled={syncingPfp || !brandTwitter.trim()}>{syncingPfp ? <Spinner size="sm" className="mr-2" /> : <IconRotateClockwise size={16} className="mr-2" />}{syncingPfp ? 'Updating...' : 'Update brand profile from X'}</Button></div></div>
 <div className="grid grid-cols-1 gap-4 md:grid-cols-2"><div className="space-y-1"><Label htmlFor="brandColor">Brand color</Label><div className="flex gap-2"><Input id="brandColor" value={config.brandColor} onChange={event => handleFieldChange('brandColor', event.target.value)} className="flex-1" /><input type="color" value={config.brandColor || '#E4CA00'} onChange={event => handleFieldChange('brandColor', event.target.value)} aria-label="Choose brand color" className="h-10 w-11 cursor-pointer border-0 p-0" /></div></div><div className="space-y-1"><Label htmlFor="expectedPerCreator">Expected impressions per creator</Label><Input id="expectedPerCreator" type="number" value={config.expectedPerCreator || ''} onChange={event => handleFieldChange('expectedPerCreator', Number(event.target.value) || 0)} /><p className="text-xs text-text-tertiary">Used to calculate expected total impressions.</p></div></div>
 <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-3"><input type="checkbox" checked={config.amplificationCard?.enabled || false} onChange={event => handleAmplificationToggle(event.target.checked)} className="h-5 w-5 border-glass-border text-accent focus:ring-accent" /><span className="text-sm font-medium text-text-primary">Show amplification comparison</span></label>
 {config.amplificationCard?.enabled && <div className="flex flex-col gap-4 border border-glass-border p-4"><h5 className="text-sm font-semibold text-text-primary">Amplified post (after)</h5><div className="grid grid-cols-1 gap-4 md:grid-cols-2"><div className="space-y-1"><Label htmlFor="ampAuthorHandle">Author handle</Label><Input id="ampAuthorHandle" value={config.amplificationCard.originalTweet.authorHandle} onChange={event => handleAmplificationChange('authorHandle', event.target.value)} /></div><div className="space-y-1"><Label htmlFor="ampAuthorName">Author name</Label><Input id="ampAuthorName" value={config.amplificationCard.originalTweet.authorName} onChange={event => handleAmplificationChange('authorName', event.target.value)} /></div><div className="space-y-1 md:col-span-2"><Label htmlFor="ampContent">Post content</Label><textarea id="ampContent" value={config.amplificationCard.originalTweet.content} onChange={event => handleAmplificationChange('content', event.target.value)} rows={2} className="flex w-full resize-y border border-glass-border bg-glass-bg px-3 py-2 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" /></div><div className="space-y-1 md:col-span-2"><Label htmlFor="ampTweetUrl">Post URL</Label><Input id="ampTweetUrl" value={config.amplificationCard.originalTweet.tweetUrl} onChange={event => handleAmplificationChange('tweetUrl', event.target.value)} placeholder="https://x.com/user/status/..." /></div></div><h5 className="text-sm font-semibold text-text-primary">Comparison content (before)</h5><p className="-mt-3 text-xs text-text-tertiary">Leave blank to reuse the amplified post.</p><div className="grid grid-cols-1 gap-4 md:grid-cols-2"><div className="space-y-1"><Label htmlFor="beforeAuthorHandle">Author handle</Label><Input id="beforeAuthorHandle" value={config.amplificationCard.beforeTweet.authorHandle} onChange={event => handleBeforeTweetChange('authorHandle', event.target.value)} /></div><div className="space-y-1"><Label htmlFor="beforeAuthorName">Author name</Label><Input id="beforeAuthorName" value={config.amplificationCard.beforeTweet.authorName} onChange={event => handleBeforeTweetChange('authorName', event.target.value)} /></div><div className="space-y-1 md:col-span-2"><Label htmlFor="beforeContent">Post content</Label><textarea id="beforeContent" value={config.amplificationCard.beforeTweet.content} onChange={event => handleBeforeTweetChange('content', event.target.value)} rows={2} className="flex w-full resize-y border border-glass-border bg-glass-bg px-3 py-2 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" /></div></div><h5 className="text-sm font-semibold text-text-primary">Before engagement</h5><div className="grid grid-cols-2 gap-4 md:grid-cols-4">{[['beforeViews', 'Views'], ['beforeReplies', 'Replies'], ['beforeRetweets', 'Retweets'], ['beforeLikes', 'Likes']].map(([field, label]) => <div key={field} className="space-y-1"><Label htmlFor={field}>{label}</Label><Input id={field} type="number" value={config.amplificationCard.originalTweet[field] || 0} onChange={event => handleAmplificationChange(field, Number(event.target.value))} /></div>)}</div><Alert variant="info"><AlertDescription>After engagement, quote-post count, and total views are calculated from creator data.</AlertDescription></Alert></div>}
 </Section>
 )}
 </div>
 </div>

 {saveError && <Alert variant="destructive"><AlertDescription>{saveError}</AlertDescription></Alert>}
 <div className="sticky bottom-0 z-10 -mx-1 flex flex-col gap-3 border border-glass-border bg-surface-1/95 p-3 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between"><p aria-live="polite" className={`text-sm ${isDirty ? 'font-medium text-warning' : 'text-text-secondary'}`}>{isDirty ? `${configDirty && dirtyGroupIds.length > 0 ? 'Report and group changes are' : configDirty ? 'Report changes are' : 'Group report changes are'} unsaved` : 'All changes saved'}</p><div className="flex flex-col-reverse gap-2 sm:flex-row"><Button type="button" variant="ghost" onClick={discardChanges} disabled={saving || !isDirty}>Discard changes</Button><Button type="button" variant="outline" onClick={() => saveChanges()} disabled={saving || !isDirty}>{saving && <Spinner size="sm" className="mr-2" />}{saving ? 'Saving...' : config.enabled ? 'Publish changes' : 'Save draft'}</Button><Button type="button" onClick={() => saveChanges({ openAfterSave: true })} disabled={saving || !reportCanOpenAfterSave}>{saving && <Spinner size="sm" className="mr-2" />}Save & open published report</Button></div></div>
 </div>
 </fieldset>
 );

 if (compact) return formContent;

 return <div className="mb-6 overflow-hidden border border-glass-border"><button type="button" className="flex min-h-[56px] w-full items-center justify-between px-6 py-4 hover:bg-glass-bg-hover" onClick={() => setAccordionOpen(previous => !previous)} aria-expanded={accordionOpen}><div className="flex flex-wrap items-center gap-3"><h3 className="text-lg font-semibold text-text-primary">Edit public report</h3><span className={`border px-2 py-0.5 text-xs font-semibold ${savedConfig.enabled ? 'border-success/30 bg-success-muted text-success' : 'border-glass-border text-text-secondary'}`}>{savedConfig.enabled ? 'LIVE' : 'OFF'}</span>{isDirty && <span className="text-xs font-medium text-warning">Unsaved changes</span>}</div><IconChevronDown size={20} className={`text-text-tertiary transition-transform ${accordionOpen ? 'rotate-180' : ''}`} /></button>{accordionOpen && <div className="border-t border-glass-border px-4 pb-6 pt-4 sm:px-6">{formContent}</div>}</div>;
};

export default PublicReportEditor;
